import { useRef, useState } from 'react';
import { api, ApiError, errorText } from '../api';
import { fehlendeFunktion, planName, upgradeAnbieten } from '../billtoro/upgrade';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { BankBald, BankKarte } from './bank/BankKarte';
import { darf, fehlerText, NurAnsehen, ohneRechtText, TarifMarke, tarifFehler } from './tarifHinweis';
import type { Abgleich, Overview, TrefferArt } from './types';

const BILLTORO = isBilltoro();

const ART: Record<TrefferArt, { label: string; cls: string; on: boolean }> = {
  sicher: { label: 'Nummer und Betrag passen', cls: 'b-ok', on: true },
  wahrscheinlich: { label: 'Name und Betrag passen', cls: 'b-ok', on: true },
  betrag_abweichend: { label: 'Nummer passt, Betrag weicht ab', cls: 'b-warn', on: false },
  nur_betrag: { label: 'Nur der Betrag passt', cls: 'b-warn', on: false },
};

// Zahlungen abgleichen (Gesetzes-Prüfung 3.3): Kontoauszug hochladen. CSV und Excel liest der Server selbst, nur die
// Zahlungseingänge, ohne KI. PDF oder Foto liest die KI, erst nach Rückfrage. Die Seite schlägt passende offene
// Rechnungen vor (gelb); bestätigte Treffer werden als Zahlung am Tag des Eingangs eingetragen, auf Wunsch mit
// Bauabzug. Billtoro: daneben „Bank verbinden“ (bank/BankKarte.tsx), ohne Ponto auf dem Server wie bisher „bald“. In der
// Kanzlei-App bleibt hier „Bank verknüpfen · bald“, dort verbindet der Mandant die Bank im Ordner „Kontoauszug“.
export function Zahlungen({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<Extract<Abgleich, { found: true }> | null>(null);
  const [on, setOn] = useState<boolean[]>([]);
  // Rest als Bauabzug eintragen (nur auf Wunsch, mit der Abrechnung des Kunden)
  const [bau, setBau] = useState<boolean[]>([]);
  // Rest als Skonto ausbuchen (vorgeschlagen, wenn der Kunde fristgerecht mit Skonto gezahlt hat)
  const [sko, setSko] = useState<boolean[]>([]);
  // PDF, Foto oder unbekanntes Format: erst fragen, bevor die KI den ganzen Auszug liest
  const [kiFrage, setKiFrage] = useState<{ file: File; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const open = data.items.filter((i) => i.kind === 'rechnung' && (i.status === 'offen' || i.status === 'teilweise')).length;
  // Billtoro: der Abgleich mit dem Kontoauszug erst ab Start (Zahlungen von Hand gehen in jedem Tarif)
  const auszugFehlt = fehlendeFunktion(data.tarif, 'kontoauszug');
  // Billtoro: PDF und Foto liest die KI nur im Tarif Pro (in Start nur CSV und Excel)
  const kiFehlt = fehlendeFunktion(data.tarif, 'ki');
  // Billtoro: hochladen und Treffer bestätigen nur mit dem Recht „Zahlungen“ (der Server prüft)
  const zahlen = darf(data.tarif, 'zahlungen');

  async function read(f: File, ki = false) {
    setBusy(true);
    setError(null);
    setRes(null);
    setKiFrage(null);
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const r = await api.post<Abgleich>(`/api/rechnungstool/${data.firm.id}/kontoauszug${ki ? '?ki=1' : ''}`, fd);
      if (!r.found) setError(r.hinweis);
      else {
        setRes(r);
        setOn(r.treffer.map((t) => ART[t.art].on));
        setBau(r.treffer.map(() => false));
        setSko(r.treffer.map((t) => !!t.skonto));
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ki_bestaetigen') setKiFrage({ file: f, text: errorText(err) });
      else setError(fehlerText(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function confirm() {
    if (!res) return;
    const items = res.treffer
      .map((t, i) => ({ t, i }))
      .filter(({ i }) => on[i])
      .map(({ t, i }) => ({
        id: t.rechnung.id,
        paidOn: t.zahlung.datum ?? data.today,
        betrag: t.zahlung.betrag,
        bauabzug: bau[i] && t.bauabzug ? t.bauabzug : null,
        skonto: sko[i] && t.skonto ? t.skonto : null,
      }));
    setSaving(true);
    try {
      const r = await api.post<{ bezahlt: number; doppelt: number }>(`/api/rechnungstool/${data.firm.id}/zahlungen`, { items });
      await reload();
      toast(
        (r.bezahlt === 1 ? 'Zahlung bei 1 Rechnung eingetragen' : `Zahlungen bei ${r.bezahlt} Rechnungen eingetragen`) +
          (r.doppelt ? `, ${r.doppelt} ${r.doppelt === 1 ? 'stand' : 'standen'} schon da` : ''),
      );
      setRes(null);
    } catch (err) {
      if (!tarifFehler(err)) toast(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  const count = on.filter(Boolean).length;
  const ohne = res ? res.ohneTreffer.filter((z) => !z.schon) : [];
  const schon = res ? res.ohneTreffer.filter((z) => z.schon) : [];
  const sum = res ? res.treffer.reduce((s, t, i) => s + (on[i] ? t.zahlung.betrag : 0), 0) : 0;

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>
            Zahlungen abgleichen <NurAnsehen tarif={data.tarif} bereich="zahlungen" />
          </h1>
          <p className="muted">
            {open ? `${open} offene ${open === 1 ? 'Rechnung' : 'Rechnungen'} warten auf Geld.` : 'Keine offenen Rechnungen.'}
          </p>
        </div>
      </div>

      <div className="rt-up">
        <div className="rt-up-card">
          <h2>
            Kontoauszug hochladen
            <TarifMarke tarif={data.tarif} fehlt={auszugFehlt} />
          </h2>
          <p>
            <b>Am besten als CSV oder Excel</b> aus Ihrem Online-Banking (Umsätze exportieren). Dann werden nur die
            Zahlungseingänge (Datum, Betrag, Zahler, Verwendungszweck) ausgewertet, ohne KI.{' '}
            {!data.ki
              ? 'PDF oder Foto geht erst, wenn die KI eingeschaltet ist.'
              : kiFehlt && kiFehlt !== 'bald' && data.tarif
                ? `PDF oder Foto liest die KI im Tarif ${planName(data.tarif, kiFehlt)}.`
                : 'Bei PDF oder Foto liest die KI den ganzen Auszug, vorher fragen wir Sie.'}
          </p>
          {zahlen ? (
            <button
              type="button"
              className="btn primary"
              disabled={busy || auszugFehlt === 'bald'}
              onClick={() => (auszugFehlt ? upgradeAnbieten(data.tarif, 'kontoauszug') : input.current?.click())}
            >
              {busy ? 'Auszug wird gelesen …' : 'Kontoauszug auswählen'}
            </button>
          ) : (
            <p className="rt-hint">{ohneRechtText('zahlungen')}</p>
          )}
          <input
            ref={input}
            type="file"
            hidden
            accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void read(f);
            }}
          />
        </div>
        {BILLTORO ? <BankKarte data={data} busy={busy} onAbgleich={read} /> : <BankBald />}
      </div>

      {error && <div className="notice warn">{error}</div>}

      {kiFrage && (
        <div className="rt-card rt-ki">
          <p style={{ marginTop: 0 }}>
            <b>{kiFrage.file.name}</b>: {kiFrage.text}
          </p>
          {data.kiTest && (
            <p className="rt-ki-test" role="note">
              Testbetrieb: Die KI läuft gerade nicht nur in der EU. Bitte nur Test-Kontoauszüge, keine echten.
            </p>
          )}
          <div className="rt-actions">
            <button type="button" className="btn" onClick={() => setKiFrage(null)}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" disabled={busy} onClick={() => void read(kiFrage.file, true)}>
              Ja, mit der KI lesen
            </button>
          </div>
        </div>
      )}

      {res && (
        <div className="rt-card rt-match">
          <p className="rt-match-sum">
            <b>{res.eingaenge}</b> {res.eingaenge === 1 ? 'Zahlungseingang' : 'Zahlungseingänge'} gelesen,{' '}
            <b>{res.treffer.length}</b> {res.treffer.length === 1 ? 'passt' : 'passen'} zu offenen Rechnungen.{' '}
            <span className="muted">{res.ki ? 'Von der KI gelesen.' : `Ohne KI gelesen${res.format ? ` (${res.format})` : ''}.`}</span>
            {res.schonEingetragen > 0 && (
              <>
                {' '}
                {res.schonEingetragen === 1 ? '1 Eingang ist' : `${res.schonEingetragen} Eingänge sind`} schon eingetragen und{' '}
                {res.schonEingetragen === 1 ? 'kommt' : 'kommen'} nicht noch einmal.
              </>
            )}
          </p>
          {res.hinweis && <p className="rt-ki-note">{res.hinweis}</p>}
          {res.treffer.length > 0 && (
            <ul className="rt-match-list">
              {res.treffer.map((t, i) => (
                <li key={i} className={on[i] ? 'on' : undefined}>
                  <label>
                    <input
                      type="checkbox"
                      checked={on[i]}
                      onChange={(e) => setOn(on.map((v, j) => (j === i ? e.target.checked : v)))}
                    />
                    <span className="rt-m-pay">
                      <span className="rt-m-amt num">{eurC(t.zahlung.betrag)}</span>
                      <span className="muted">
                        {t.zahlung.datum ? fmtDate(t.zahlung.datum) : 'ohne Datum'} · {t.zahlung.name ?? 'unbekannt'}
                      </span>
                      {t.zahlung.zweck && <span className="rt-m-zweck">{t.zahlung.zweck}</span>}
                    </span>
                    <span className="rt-m-arrow" aria-hidden>
                      →
                    </span>
                    <span className="rt-m-inv">
                      <span className="num">
                        <b>{t.rechnung.number}</b>
                      </span>
                      <span>{t.rechnung.customer}</span>
                      <span className="muted">offen {eurC(t.rechnung.open)}</span>
                      <span className={`badge ${ART[t.art].cls}`}>{ART[t.art].label}</span>
                    </span>
                  </label>
                  {t.skonto != null && t.skonto > 0 && on[i] && (
                    <label className="check rt-m-bau">
                      <input
                        type="checkbox"
                        checked={sko[i]}
                        onChange={(e) => {
                          setSko(sko.map((v, j) => (j === i ? e.target.checked : v)));
                          // entweder Skonto oder Bauabzug
                          if (e.target.checked) setBau(bau.map((v, j) => (j === i ? false : v)));
                        }}
                      />
                      <span>
                        Rest {eurC(t.skonto)} als Skonto ausbuchen
                        <br />
                        <small className="muted">Der Kunde hat bis zur Skontofrist gezahlt und das Skonto der Rechnung abgezogen. Danach ist sie bezahlt.</small>
                      </span>
                    </label>
                  )}
                  {t.bauabzug != null && t.bauabzug > 0 && on[i] && (
                    <label className="check rt-m-bau">
                      <input
                        type="checkbox"
                        checked={bau[i]}
                        onChange={(e) => {
                          setBau(bau.map((v, j) => (j === i ? e.target.checked : v)));
                          if (e.target.checked) setSko(sko.map((v, j) => (j === i ? false : v)));
                        }}
                      />
                      <span>
                        Rest {eurC(t.bauabzug)} als Bauabzug eintragen
                        <br />
                        <small className="muted">Nur wenn Ihr Kunde 15 % ans Finanzamt gezahlt und Ihnen die Abrechnung geschickt hat.</small>
                      </span>
                    </label>
                  )}
                </li>
              ))}
            </ul>
          )}
          {[
            [ohne, `${ohne.length} ${ohne.length === 1 ? 'Eingang' : 'Eingänge'} ohne passende Rechnung`] as const,
            [schon, `${schon.length} schon eingetragen`] as const,
          ].map(
            ([list, title]) =>
              list.length > 0 && (
                <details className="rt-nomatch" key={title}>
                  <summary>{title}</summary>
                  <ul>
                    {list.map((z, i) => (
                      <li key={i}>
                        <span className="num">{eurC(z.betrag)}</span> · {z.datum ? fmtDate(z.datum) : 'ohne Datum'} ·{' '}
                        {z.name ?? 'unbekannt'}
                        {z.zweck ? ` · ${z.zweck}` : ''}
                        {z.schon && <span className="muted"> · steht bei {z.schon}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              ),
          )}
          <div className="rt-actions">
            <button type="button" className="btn" onClick={() => setRes(null)}>
              Verwerfen
            </button>
            {res.treffer.length > 0 && (
              <button type="button" className="btn primary" disabled={!count || saving} onClick={() => void confirm()}>
                {count === 1 ? '1 Zahlung' : `${count} Zahlungen`} eintragen{count ? ` (${eurC(sum)})` : ''}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
